import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button, Tooltip, makeStyles } from '@material-ui/core';
import VideoOffIcon from '../../../icons/VideoOffIcon';
import VideoOnIcon from '../../../icons/VideoOnIcon';

import useDevices from '../../../hooks/useDevices/useDevices';
import useLocalVideoToggle from '../../../hooks/useLocalVideoToggle/useLocalVideoToggle';
import { useSmartDeviceDetection, DeviceState } from '../../../hooks/useSmartDeviceDetection/useSmartDeviceDetection';
import { openWindowsSettingsForError, getBrowser } from '../../../utils/deviceSettings';
import { DevicePermissionModal } from '../../../components/DevicePermissionModal/DevicePermissionModal';
import { useSystemMuteNotification } from '../../../hooks/useSystemMuteNotification/useSystemMuteNotification';
import { SystemMuteToast } from '../../../components/SystemMuteToast/SystemMuteToast';

const useStyles = makeStyles(theme => ({
  toggleButton: {
    position: 'relative',
    transition: 'all 0.2s ease',
  },
  iconButton: {
    display: 'flex',
    alignItems: 'center',
    minWidth: '95px !important',
    [theme.breakpoints.down('sm')]: {
      minHeight: '45px !important',
    },
  },
  errorButton: {
    backgroundColor: '#fef7e0 !important', // Light orange background like Google Meet
    borderColor: '#f9ab00 !important', // Orange border
    border: '2px solid !important',
    color: '#b06000 !important',
    '&:hover': {
      backgroundColor: '#fde293 !important',
      borderColor: '#ea8600 !important',
    },
    '&:disabled': {
      backgroundColor: '#fef7e0 !important',
      borderColor: '#f9ab00 !important',
      color: '#b06000 !important',
      opacity: 0.7,
    },
  },
  warningBadge: {
    position: 'absolute',
    top: -10,
    right: -10,
    backgroundColor: '#f9ab00', // Google Meet yellow
    color: 'white',
    width: 20,
    height: 20,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 'bold',
    zIndex: 1,
    border: '2px solid white',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
  },
  tooltipContainer: {
    position: 'relative',
    '& .MuiTooltip-popper': {
      zIndex: 1500, // Ensure tooltip appears above other elements
    },
    '& .MuiTooltip-tooltip': {
      marginTop: '-1px !important', // Reduce the gap
      marginBottom: '-1px !important',
    },
  },
}));

interface EnhancedToggleVideoButtonProps {
  disabled?: boolean;
  className?: string;
  buttonText?: boolean;
}

export default function EnhancedToggleVideoButton({ disabled, className, buttonText }: EnhancedToggleVideoButtonProps) {
  const classes = useStyles();
  const [isVideoEnabled, toggleVideoEnabled] = useLocalVideoToggle();
  const lastClickTimeRef = useRef(0);
  const { hasVideoInputDevices } = useDevices();
  const { videoError, recheckDevice } = useSmartDeviceDetection();

  // System mute notification hook
  const {
    showVideoToast,
    showSystemMuteToast,
    hideSystemMuteToast,
    resetDismissedToasts,
  } = useSystemMuteNotification();

  // Modal state
  const [showModal, setShowModal] = useState(false);

  const hasError = videoError !== null;

  // Show system mute toast when system blocking is detected
  useEffect(() => {
    if (videoError?.state === DeviceState.SYSTEM_BLOCKED) {
      showSystemMuteToast('camera', videoError);
    }
  }, [videoError, showSystemMuteToast]);

  // Reset dismissed toasts when error is resolved
  useEffect(() => {
    if (!videoError) {
      resetDismissedToasts();
    }
  }, [videoError, resetDismissedToasts]);

  const toggleVideo = useCallback(() => {
    if (Date.now() - lastClickTimeRef.current > 500) {
      lastClickTimeRef.current = Date.now();
      toggleVideoEnabled();
    }
  }, [toggleVideoEnabled]);

  const handleClick = () => {
    if (hasError && videoError?.actionable) {
      console.log('Handling error action for state:', videoError.state);
      handleErrorAction();
    } else {
      toggleVideo();
    }
  };

  const handleErrorAction = () => {
    if (!videoError) return;

    console.log('🔧 handleErrorAction called with error:', videoError);

    switch (videoError.state) {
      case DeviceState.PERMISSION_DENIED:
        console.log('Permission denied - showing modal');
        setShowModal(true);
        break;

      case DeviceState.SYSTEM_BLOCKED:
      case DeviceState.DEVICE_UNPLUGGED:
      case DeviceState.NO_DEVICE:
      case DeviceState.DEVICE_IN_USE:
        console.log(`${videoError.state} - opening smart Windows settings`);
        if (videoError.canOpenSettings) {
          // FIXED: Use 'camera' instead of 'microphone'
          openWindowsSettingsForError('camera', videoError.state);
        }
        break;

      default:
        console.log('Unknown error - generic retry');
        // FIXED: Use 'camera' instead of 'microphone'
        recheckDevice('camera');
    }
  };

  const handleSystemMuteLearnMore = () => {
    // Same action as clicking the button when system blocked
    if (videoError?.canOpenSettings) {
      openWindowsSettingsForError('camera', videoError.state);
    }
  };

  const getButtonText = () => {
    if (hasError) {
      switch (videoError?.state) {
        case DeviceState.PERMISSION_DENIED:
          return 'Enable Camera';
        case DeviceState.NO_DEVICE:
          return 'No Camera';
        case DeviceState.DEVICE_IN_USE:
          return 'Camera In Use';
        case DeviceState.DEVICE_UNPLUGGED:
          return 'Camera Disconnected';
        default:
          return 'Camera Error';
      }
    }

    if (!hasVideoInputDevices) return 'No Video';
    return isVideoEnabled ? 'Stop Video' : 'Start Video';
  };

  const getTooltipText = () => {
    if (hasError) {
      return videoError?.message || 'Camera error - click to fix';
    }
    return isVideoEnabled ? 'Turn off camera' : 'Turn on camera';
  };

  // Apply error styling if there's an error
  const buttonClasses = [
    buttonText ? classes.toggleButton : classes.iconButton,
    hasError ? classes.errorButton : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <Tooltip arrow={true} placement="top" title={getTooltipText()}>
        <div className={classes.tooltipContainer}>
          <Button
            className={buttonClasses}
            onClick={handleClick}
            disabled={(!hasVideoInputDevices && !hasError) || disabled}
            startIcon={isVideoEnabled ? <VideoOnIcon /> : <VideoOffIcon />}
          >
            {buttonText && getButtonText()}
          </Button>

          {/* Google Meet style warning badge */}
          {hasError && <div className={classes.warningBadge}>!</div>}
        </div>
      </Tooltip>

      {/* Google Meet style permission modal */}
      <DevicePermissionModal
        open={showModal}
        onClose={() => setShowModal(false)}
        deviceType="camera"
        browser={getBrowser()}
      />

      {/* Google Meet style system mute toast */}
      <SystemMuteToast
        open={showVideoToast}
        onClose={() => hideSystemMuteToast('camera')}
        onLearnMore={handleSystemMuteLearnMore}
        deviceType="camera"
      />
    </>
  );
}
